// Shared "Was ändert sich, wenn…"-Szenario-Panel.
// Wird im Dashboard (InquiriesPage) UND im Konfigurator genutzt.
// Via `endpoint`-Prop bestimmbar, welcher Backend-Route angefragt wird.
import { useEffect, useState } from 'react';
import { Checkbox } from './ui/checkbox';
import { SlidersHorizontal, RefreshCw } from 'lucide-react';
import { API_BASE } from '../lib/api-base';

// ─────────────────────────────────────────────────────
// Lastprofil-Bibliothek (SLP-angelehnt, deterministisch)
// Jede Funktion liefert den stündlichen Anteil 0..∞ —
// wird anschließend auf den Tagesverbrauch normalisiert.
// ─────────────────────────────────────────────────────
const PROFILE_CURVES = {
  // H25 — typischer Haushalt (EFH)
  private_efh: (h) => {
    const base = (h < 6 || h > 21) ? 0.45 : 0.15;
    const morning = Math.exp(-Math.pow((h - 7) / 1.2, 2)) * 1.2;
    const evening = Math.exp(-Math.pow((h - 19.5) / 2.0, 2)) * 3.5;
    return base + morning + evening;
  },
  // Villa / Altbau — wie EFH, aber mehr Tag-Grundlast (größere Wohnfläche)
  private_villa: (h) => {
    const base = (h < 6 || h > 21) ? 0.55 : 0.30;
    const morning = Math.exp(-Math.pow((h - 7) / 1.2, 2)) * 1.4;
    const evening = Math.exp(-Math.pow((h - 19.5) / 2.0, 2)) * 3.8;
    return base + morning + evening;
  },
  // MFH Allgemeinstrom — flacher, Treppenhauslicht + Keller
  private_mfh: (h) => {
    const base = 0.6;
    const morning = Math.exp(-Math.pow((h - 7) / 1.5, 2)) * 0.9;
    const evening = Math.exp(-Math.pow((h - 20) / 2.4, 2)) * 1.4;
    return base + morning + evening;
  },
  // G1 Büro — Werktags 8–18 Uhr
  office: (h) => {
    const base = 0.1;
    const work = (h >= 7 && h <= 18)
      ? 1.2 * Math.exp(-Math.pow((h - 12) / 4.5, 2))
      : 0;
    return base + work;
  },
  // Werkstatt — wie Büro + Morgenanlauf
  workshop: (h) => {
    const base = 0.15;
    const work = (h >= 6 && h <= 17)
      ? 1.8 * Math.exp(-Math.pow((h - 10) / 3.5, 2))
      : 0;
    return base + work;
  },
  // G3 Dauerbetrieb — 24/7 flache Grundlast
  production: () => 1.0,
  // G4 Handel — 9–19 Uhr mit Abendpeak
  retail: (h) => {
    const base = 0.25;
    const open = (h >= 8 && h <= 20)
      ? 1.4 * Math.exp(-Math.pow((h - 14) / 5, 2))
      : 0;
    return base + open;
  },
  // G2 Gastro/Hotel — Abend + Wochenende
  gastro: (h) => {
    const base = 0.4;
    const lunch = Math.exp(-Math.pow((h - 13) / 1.3, 2)) * 1.2;
    const dinner = Math.exp(-Math.pow((h - 20) / 1.8, 2)) * 3.5;
    return base + lunch + dinner;
  },
  // L1 Landwirtschaft mit Tierhaltung — durchgehend mit Melk-Peaks
  agri_livestock: (h) => {
    const base = 0.6;
    const morning = Math.exp(-Math.pow((h - 5.5) / 1.2, 2)) * 2.0;  // Melken früh
    const evening = Math.exp(-Math.pow((h - 17) / 1.2, 2)) * 1.8;   // Melken abends
    return base + morning + evening;
  },
  // L0/L2 Landwirtschaft allgemein — Tageslicht-getrieben
  agri_general: (h) => {
    const base = 0.3;
    const work = (h >= 6 && h <= 20)
      ? 1.2 * Math.exp(-Math.pow((h - 12) / 4, 2))
      : 0;
    return base + work;
  },
};

const PROFILE_LABELS = {
  private_efh: 'Einfamilienhaus (H25)',
  private_villa: 'Villa / Altbau',
  private_mfh: 'Mehrfamilienhaus (Allgemeinstrom)',
  office: 'Büro / Praxis (G1)',
  workshop: 'Werkstatt / Handwerk',
  production: 'Produktion / 24/7 (G3)',
  retail: 'Handel / Einzelhandel (G4)',
  gastro: 'Gastronomie / Hotel (G2)',
  agri_livestock: 'Landwirtschaft mit Tierhaltung (L1)',
  agri_general: 'Landwirtschaft allgemein (L0/L2)',
};

export default function WhatIfPanel({
  initialPvKwp, initialBuildingType, initialHasExistingPv,
  initialProfileClass = 'private_efh',
  initialWantedKwh = null,    // Original-wantedKwh aus Mail-Klassifikation,
                              // damit erste Empfehlung identisch zur Anfrage ist
  initialBackupKw = 0,         // Notstrom-Peak aus Mail-Klassifikation
  initialPinnedModel = null,   // Vorausgewähltes Modell aus Mail-Klassifikation
  endpoint = '/api/mail-inbound/what-if',
  authHeader = null,          // optional: fetch-wrapper für Auth (Dashboard)
  showApproximationNote = false,
}) {
  // Maximalwerte für die Slider
  const MAX_PV_KWP = 70;
  const MAX_ANNUAL_KWH = 100000;
  const MAX_BACKUP_KW = 70;

  const [pvKwp, setPvKwp] = useState(initialPvKwp || 7);
  const [annualKwh, setAnnualKwh] = useState(0);
  const [backupKw, setBackupKw] = useState(initialBackupKw || 0);
  const [buildingType, setBuildingType] = useState(initialBuildingType || 'standard');
  const [hasExistingPv, setHasExistingPv] = useState(!!initialHasExistingPv);
  const [eastWest, setEastWest] = useState(false);
  const [profileClass, setProfileClass] = useState(initialProfileClass);
  const [extraModules, setExtraModules] = useState(0);   // 0..6 × 5.12 kWh
  const [moduleManual, setModuleManual] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(false);
  // Initial-wantedKwh wird genau einmal an das Backend übergeben — beim
  // ersten Render. Sobald der User Slider verstellt, schalten wir es ab,
  // damit die Slider-Änderungen die Empfehlung beeinflussen können.
  const [useInitialWanted, setUseInitialWanted] = useState(!!initialWantedKwh);
  // Pinned-Model: gleiche Logik — initial 1:1 zur Mail-Empfehlung, dann frei.
  const [usePinnedModel, setUsePinnedModel] = useState(!!initialPinnedModel);

  // Werte über dem Limit → manuelle Prüfung nötig.
  const overLimit = (initialPvKwp > MAX_PV_KWP)
    || (initialBackupKw > MAX_BACKUP_KW)
    || ((initialWantedKwh || 0) > MAX_ANNUAL_KWH / 10);

  // Solange der Nutzer den Modul-Slider nicht manuell bewegt hat, folgt
  // der Wert automatisch der Backend-Empfehlung. Sobald der Nutzer die Zahl
  // selbst anpasst, bleibt seine Auswahl bestehen.
  useEffect(() => {
    if (!moduleManual && result?.recommendedAdditionalModules != null) {
      setExtraModules(result.recommendedAdditionalModules);
    }
  }, [result, moduleManual]);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (authHeader) Object.assign(headers, authHeader);
        const url = endpoint.startsWith('http')
          ? endpoint
          : `${API_BASE}${endpoint}`;
        const r = await fetch(url, {
          method: 'POST', headers,
          body: JSON.stringify({
            pvKwp: pvKwp || null, annualKwh: annualKwh || null,
            backupKw: backupKw || null, buildingType,
            hasExistingPv, eastWest,
            profileClass,   // damit WhatIf-Backend dieselbe Familie wie der Main-Konfigurator wählt
            // Beim ersten Render: directWantedKwh aus Original-Mail mitschicken,
            // damit die Empfehlung exakt identisch zur Produktempfehlung ist.
            directWantedKwh: useInitialWanted && initialWantedKwh ? initialWantedKwh : null,
            // Pinned-Model: ebenso beim ersten Render, damit das Panel exakt
            // mit der Mail-Empfehlung öffnet (z. B. GS 60 bei Notstrom-Mail).
            pinnedModel: usePinnedModel && initialPinnedModel ? initialPinnedModel : null,
          }),
        });
        if (r.ok) {
          const d = await r.json();
          setResult(d);
          setSelectedModel(d?.model || null);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [pvKwp, annualKwh, backupKw, buildingType, hasExistingPv, eastWest, profileClass, endpoint, authHeader, useInitialWanted, initialWantedKwh, usePinnedModel, initialPinnedModel]);

  // Sobald der User irgendeinen Slider verstellt, deaktivieren wir den
  // Initial-wantedKwh-Override UND den Pinned-Model-Override → ab dann
  // greifen die normalen Heuristiken.
  const onSliderChange = (setter) => (value) => {
    setUseInitialWanted(false);
    setUsePinnedModel(false);
    setter(value);
  };

  const Slider = ({ label, unit, min, max, step, value, setValue, testId }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-gray-600 font-medium">{label}</span>
        <span className="text-[11px] tabular-nums text-[#9ec043] font-semibold">
          {value || 0} {unit}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value || 0}
             onChange={e => setValue(parseFloat(e.target.value))}
             className="w-full accent-[#9ec043] h-1.5 cursor-pointer"
             data-testid={testId} />
    </div>
  );

  return (
    <div className="mt-3 border border-gray-200 rounded-lg bg-gray-50/50 p-3 no-print"
         data-testid="whatif-panel" data-no-pdf="true">
      <div className="flex items-center gap-1.5 mb-3">
        <SlidersHorizontal className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">
          Was ändert sich, wenn…
        </span>
        {loading && <RefreshCw className="w-3 h-3 text-gray-400 animate-spin ml-1" />}
      </div>
      {overLimit && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-relaxed"
             data-testid="whatif-over-limit">
          ⚠ Die Anfrage enthält Werte, die über dem Sandbox-Limit liegen
          (max. {MAX_PV_KWP} kWp PV / {MAX_ANNUAL_KWH.toLocaleString('de-DE')} kWh
          Jahresverbrauch / {MAX_BACKUP_KW} kW Notstrom). „Was ändert sich, wenn…"
          arbeitet hier mit den gekappten Werten — bitte manuell prüfen.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <Slider label="PV-Leistung" unit="kWp" min={0} max={MAX_PV_KWP} step={0.5}
                value={pvKwp} setValue={onSliderChange(setPvKwp)} testId="whatif-pv" />
        <Slider label="Jahresverbrauch" unit="kWh" min={0} max={MAX_ANNUAL_KWH} step={1000}
                value={annualKwh} setValue={onSliderChange(setAnnualKwh)} testId="whatif-kwh" />
        <Slider label="Notstrom-Peak" unit="kW" min={0} max={MAX_BACKUP_KW} step={1}
                value={backupKw} setValue={onSliderChange(setBackupKw)} testId="whatif-backup" />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] mb-3">
        <label className="text-gray-600 font-medium">Lastprofil:</label>
        <select value={profileClass} onChange={e => setProfileClass(e.target.value)}
                className="border border-gray-200 rounded px-2 py-1 text-[11px] bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                data-testid="whatif-profile-class">
          {Object.entries(PROFILE_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <span className="w-px h-3 bg-gray-200" />
        <label className="text-gray-600 font-medium">Gebäudetyp:</label>
        {[
          { v: 'standard', l: 'Standard' },
          { v: 'villa', l: 'Villa / Altbau (+30 %)' },
          { v: 'multi_family', l: 'Mehrfamilienhaus (+50 %)' },
        ].map(o => (
          <label key={o.v} className="inline-flex items-center gap-1 cursor-pointer">
            <input type="radio" checked={buildingType === o.v}
                   onChange={() => setBuildingType(o.v)}
                   className="accent-[#9ec043]"
                   data-testid={`whatif-bt-${o.v}`} />
            <span className={buildingType === o.v ? 'text-[#7ca735] font-semibold' : 'text-gray-600'}>
              {o.l}
            </span>
          </label>
        ))}
        <span className="w-px h-3 bg-gray-200" />
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <Checkbox checked={hasExistingPv} onCheckedChange={v => setHasExistingPv(!!v)}
                    data-testid="whatif-existing-pv" />
          <span className="text-gray-600">Bestehende PV (AC)</span>
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <Checkbox checked={eastWest} onCheckedChange={v => setEastWest(!!v)}
                    data-testid="whatif-east-west" />
          <span className="text-gray-600">Ost/West-Dach</span>
        </label>
      </div>
      {result?.model && (() => {
        const options = [
          { model: result.model, usableKwh: result.usableKwh, kind: 'primary' },
          ...(result.alternatives || []).filter(a => a.model && a.usableKwh),
        ];
        const active = options.find(o => o.model === selectedModel) || options[0];
        return (
        <div className="bg-white border border-[#9ec043]/30 rounded-md p-2.5" data-testid="whatif-result">
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <span className="text-sm font-semibold text-[#32373c]">→ {result.model}{extraModules > 0 ? ` + ${extraModules}× Modul` : ''}</span>
            <span className="text-[10px] text-gray-400">
              Zielkapazität {Number(result.wantedKwh).toFixed(1)} kWh · {(active.usableKwh + extraModules * 4.864).toFixed(2)} kWh nutzbar{extraModules > 0 ? ` (${active.usableKwh} + ${(extraModules * 4.864).toFixed(2)})` : ''}
            </span>
          </div>
          {options.length > 1 && (
            <div className="flex flex-wrap gap-1 text-[10px]" data-testid="whatif-model-tabs">
              {options.map((o, i) => {
                const isActive = o.model === active.model;
                const isCommercial = o.kind === 'commercial';
                const base = 'px-1.5 py-0.5 rounded cursor-pointer transition-all';
                const cls = isActive
                  ? (isCommercial ? 'bg-amber-500 text-white font-semibold' : 'bg-[#9ec043] text-white font-semibold')
                  : (isCommercial ? 'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200');
                return (
                  <button key={i} type="button" onClick={() => setSelectedModel(o.model)}
                          className={`${base} ${cls}`}
                          title={(result.alternatives?.find(a => a.model === o.model)?.reason) || 'Empfohlenes System'}
                          data-testid={isActive ? 'whatif-tab-active' : `whatif-tab-${o.model.replace(/\s+/g, '-')}`}>
                    {isCommercial ? '⚡ ' : ''}{o.model}
                  </button>
                );
              })}
            </div>
          )}
          {result.commercialHint && (
            <div className="mt-2 border-t border-amber-200 pt-2 text-[11px] text-amber-800 bg-amber-50 -mx-2.5 -mb-2.5 px-2.5 py-1.5 rounded-b-md leading-relaxed"
                 data-testid="whatif-commercial-hint">
              ⚠️ <strong>Gewerbesystem empfohlen:</strong> {result.commercialHint}
            </div>
          )}

          {result.gsPvNote && (
            <div className="mt-2 border border-sky-200 bg-sky-50 rounded-md px-2.5 py-1.5 text-[11px] text-sky-900 leading-relaxed"
                 data-testid="whatif-gs-pv-note">
              🔌 <strong>PV-Einbindung am GS (Neuanlage):</strong> Da die PV-Anlage neu errichtet wird, ist entweder ein <strong>PV-Direkteinbindungsmodul</strong> für den Hans GS notwendig oder ein <strong>externer PV-AC-Wechselrichter</strong>. Das PV-Direkteinbindungsmodul ist projektspezifisch — bitte über EES (<a href="mailto:order@ees-energiespeicher.de" className="underline">order@ees-energiespeicher.de</a>) anfragen.
            </div>
          )}

          {/* Zusätzliche Batteriemodule (5.12 kWh brutto / 4.864 nutzbar) —
              beeinflusst die Simulation und die angezeigte nutzbare Kapazität. */}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-gray-600 font-medium">
                Zusätzliche Batteriemodule (5,12 kWh brutto / 4,86 kWh nutzbar)
              </span>
              <span className="text-[11px] tabular-nums text-[#9ec043] font-semibold">
                {extraModules} × 4,86 kWh = +{(extraModules * 4.864).toFixed(2)} kWh
              </span>
            </div>
            <input type="range" min={0} max={6} step={1} value={extraModules}
                   onChange={e => { setExtraModules(parseInt(e.target.value, 10)); setModuleManual(true); }}
                   className="w-full accent-[#9ec043] h-1.5 cursor-pointer"
                   data-testid="whatif-extra-modules" />
            {result.recommendedAdditionalModules > 0 && extraModules !== result.recommendedAdditionalModules && (
              <div className="text-[10px] text-gray-400 italic mt-0.5">
                Empfohlen: +{result.recommendedAdditionalModules} Modul{result.recommendedAdditionalModules > 1 ? 'e' : ''} für die berechnete Zielkapazität.
              </div>
            )}
          </div>

          <DaySimChart key={`${active.model}-${profileClass}-${extraModules}`}
                       pvKwp={pvKwp} annualKwh={annualKwh}
                       usableKwh={(active.usableKwh || 0) + extraModules * 4.864}
                       activeModel={active.model}
                       profileClass={profileClass} />
          {showApproximationNote && (
            <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400 italic leading-snug">
              ℹ️ Näherungswerte: Die Simulation basiert auf typischen Haushaltsprofilen und
              Durchschnitts-Ertragsdaten (≈ 950 kWh/kWp/Jahr, DE). Individuelle Werte können je
              nach Standort, Verbrauchsverhalten und Dachausrichtung abweichen.
            </div>
          )}
        </div>
        );
      })()}
      {result && !result.model && (
        <p className="text-[11px] text-gray-400 italic">Keine passende Empfehlung für diese Parameter.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// DaySimChart — 24h-Simulation (PV, Verbrauch, Speicher-SoC)
// ─────────────────────────────────────────────────────
function DaySimChart({ pvKwp, annualKwh, usableKwh, activeModel, profileClass = 'private_efh' }) {
  const hours = 24;
  const fallbackAnnual = Math.max(4000, (pvKwp || 0) * 1100);
  const daily = (annualKwh || fallbackAnnual) / 365;
  const dailyPv = (pvKwp || 0) * 2.6;

  const pvProfile = Array.from({ length: hours }, (_, h) => {
    if (h < 6 || h > 19) return 0;
    return Math.pow(Math.sin(((h - 6) / 13) * Math.PI), 2);
  });
  const pvSum = pvProfile.reduce((a, b) => a + b, 0) || 1;
  // Profil-spezifisches Lastprofil (SLP-angelehnt). Fallback: private_efh.
  const curveFn = PROFILE_CURVES[profileClass] || PROFILE_CURVES.private_efh;
  const loadProfile = Array.from({ length: hours }, (_, h) => curveFn(h));
  const loadSum = loadProfile.reduce((a, b) => a + b, 0) || 1;

  const pv = pvProfile.map(p => (p / pvSum) * dailyPv);
  const load = loadProfile.map(l => (l / loadSum) * daily);

  const eff = 0.9;
  let soc = 0;
  const socArr = [];
  let selfConsumed = 0;
  for (let h = 0; h < hours; h++) {
    const surplus = pv[h] - load[h];
    if (surplus > 0) {
      const room = (usableKwh || 0) - soc;
      const charge = Math.min(surplus * eff, room);
      soc += charge;
      selfConsumed += Math.min(pv[h], load[h]);
    } else if (surplus < 0) {
      const need = -surplus;
      const fromBattery = Math.min(need, soc);
      soc -= fromBattery;
      selfConsumed += pv[h] + fromBattery;
    } else {
      selfConsumed += pv[h];
    }
    socArr.push(soc);
  }
  const autarkyPct = daily > 0 ? Math.min(100, (selfConsumed / daily) * 100) : 0;
  const selfUsePct = dailyPv > 0 ? Math.min(100, (selfConsumed / dailyPv) * 100) : 0;

  const W = 260, H = 96, PAD = 2, TOP = 10;
  const maxY = Math.max(...pv, ...load, ...socArr, 1) * 1.1;
  const scaleX = i => PAD + (i / (hours - 1)) * (W - 2 * PAD);
  const scaleY = v => H - PAD - (v / maxY) * (H - PAD - TOP);
  const toPath = (arr) => arr.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${scaleX(i).toFixed(1)},${scaleY(v).toFixed(1)}`).join(' ');

  return (
    <div className="mt-2 border-t border-gray-100 pt-2" data-testid="daysim-chart">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
          Tagessimulation{activeModel ? ` · ${activeModel}` : ''}
        </span>
        <span className="text-[10px] tabular-nums text-gray-500">
          Autarkie <strong className="text-[#7ca735]">{autarkyPct.toFixed(0)} %</strong>
          <span className="mx-1.5">·</span>
          Eigenverbrauch <strong className="text-[#7ca735]">{selfUsePct.toFixed(0)} %</strong>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none">
        {[0, 6, 12, 18, 23].map(h => (
          <text key={`lbl-${h}`} x={scaleX(h)} y={7} fontSize="6.5" fill="#9ca3af"
                textAnchor={h === 0 ? 'start' : h === 23 ? 'end' : 'middle'}>
            {h === 23 ? '24 h' : `${h} h`}
          </text>
        ))}
        {[6, 12, 18].map(h => (
          <line key={h} x1={scaleX(h)} y1={TOP} x2={scaleX(h)} y2={H - PAD}
                stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="1.5 1.5" />
        ))}
        <path d={`${toPath(load)} L${scaleX(hours - 1)},${H - PAD} L${scaleX(0)},${H - PAD} Z`}
              fill="#9ca3af" fillOpacity="0.15" stroke="none" />
        <path d={toPath(load)} fill="none" stroke="#6b7280" strokeWidth="1" strokeLinejoin="round" />
        <path d={toPath(pv)} fill="none" stroke="#9ec043" strokeWidth="1.4" strokeLinejoin="round" />
        <path d={toPath(socArr)} fill="none" stroke="#f59e0b" strokeWidth="1"
              strokeDasharray="2 1.5" strokeLinejoin="round" />
      </svg>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[9.5px] text-gray-500">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 bg-[#9ec043]" /> PV-Erzeugung</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 bg-gray-500" /> Verbrauch</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-0.5 bg-amber-500" style={{ borderTop: '1px dashed #f59e0b', height: 0 }} />
          Speicher-Füllstand
        </span>
      </div>
    </div>
  );
}
